import { useEffect } from 'react';

import { useInfiniteQuery } from '@tanstack/react-query';

import {
  groupPayerNetworkStatusQueryKeys,
  indexFacilityNetworkSummaries,
} from 'services/api/groupPayerNetworkStatuses';

const PAGE_SIZE = 100;

// Every allowed facility, every page, so the add flows' Select all covers the whole list.
// ponytail: loads all pages up front; past a few thousand facilities, move search and select-all to the server.
export function useFacilityRows({ stediPayerId, enabled }: { stediPayerId?: string; enabled: boolean }) {
  const params = { pageSize: PAGE_SIZE, ...(stediPayerId ? { stediPayerId } : {}) };
  const query = useInfiniteQuery({
    queryKey: groupPayerNetworkStatusQueryKeys.facilities({ ...params, everyPage: true }),
    queryFn: ({ pageParam, signal }) => indexFacilityNetworkSummaries({ ...params, page: pageParam }, signal),
    initialPageParam: 1,
    getNextPageParam: (last, pages) => (pages.length < last.meta.totalPages ? pages.length + 1 : undefined),
    enabled,
  });

  // A failed page stops the walk: hasNextPage stays true after one, so retrying here would loop.
  const { hasNextPage, isFetchingNextPage, isError, fetchNextPage } = query;
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isError) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, fetchNextPage]);

  return {
    facilities: query.data?.pages.flatMap((page) => page.data) ?? [],
    loaded: query.isSuccess && !hasNextPage,
    failed: isError,
  };
}
